import type { HealthResponse, Todo } from "@repo/example-shared";
import { useEffect, useState } from "react";

import { createTodo, deleteTodo, getHealth, getTodos, updateTodo } from "./api";

export function App() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const [healthRes, todosRes] = await Promise.all([
          getHealth(),
          getTodos(),
        ]);

        if (healthRes.success && healthRes.data) {
          setHealth(healthRes.data);
        } else {
          setError(healthRes.error?.message ?? "Health check failed");
          return;
        }

        if (todosRes.success && todosRes.data) {
          setTodos(todosRes.data.items);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to connect to API",
        );
      } finally {
        setLoading(false);
      }
    }

    void fetchData();
  }, []);

  async function handleAdd() {
    const title = newTitle.trim();
    if (!title) {
      return;
    }

    setAdding(true);
    try {
      const res = await createTodo({ title });
      if (res.success && res.data) {
        setTodos((prev) => [res.data!, ...prev]);
        setNewTitle("");
      }
    } catch {
      setError("Failed to create todo");
    } finally {
      setAdding(false);
    }
  }

  async function handleToggle(todo: Todo) {
    try {
      const res = await updateTodo(todo.id, { completed: !todo.completed });
      if (res.success && res.data) {
        setTodos((prev) => prev.map((t) => (t.id === todo.id ? res.data! : t)));
      }
    } catch {
      setError("Failed to update todo");
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteTodo(id);
      setTodos((prev) => prev.filter((t) => t.id !== id));
    } catch {
      setError("Failed to delete todo");
    }
  }

  return (
    <div
      style={{
        fontFamily: "system-ui, sans-serif",
        padding: "2rem",
        maxWidth: "600px",
        margin: "0 auto",
      }}
    >
      <h1>Todo App</h1>

      {loading && <p>Loading...</p>}

      {error && (
        <div style={{ color: "red", marginBottom: "1rem" }}>
          <p>Error: {error}</p>
          <p style={{ fontSize: "0.875rem", color: "#666" }}>
            Make sure the backend is running: pnpm --filter
            @repo/example-backend dev
          </p>
        </div>
      )}

      {health && (
        <div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleAdd();
            }}
            style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}
          >
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="What needs to be done?"
              disabled={adding}
              style={{
                flex: 1,
                padding: "0.5rem",
                fontSize: "1rem",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
            <button
              type="submit"
              disabled={adding || !newTitle.trim()}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "1rem",
                cursor: "pointer",
              }}
            >
              {adding ? "Adding..." : "Add"}
            </button>
          </form>

          {todos.length === 0 ? (
            <p style={{ color: "#666", fontSize: "0.875rem" }}>
              No todos yet. Add one above!
            </p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0 }}>
              {todos.map((todo) => (
                <li
                  key={todo.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    padding: "0.5rem 0",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={todo.completed}
                    onChange={() => void handleToggle(todo)}
                    aria-label={`Toggle ${todo.title}`}
                  />
                  <span
                    style={{
                      flex: 1,
                      textDecoration: todo.completed ? "line-through" : "none",
                      color: todo.completed ? "#999" : "inherit",
                    }}
                  >
                    {todo.title}
                  </span>
                  <button
                    onClick={() => void handleDelete(todo.id)}
                    aria-label={`Delete ${todo.title}`}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#c00",
                      cursor: "pointer",
                      fontSize: "1rem",
                    }}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}

          <details style={{ marginTop: "1.5rem" }}>
            <summary style={{ cursor: "pointer", color: "#666" }}>
              API Status
            </summary>
            <table
              style={{
                borderCollapse: "collapse",
                fontSize: "0.875rem",
                marginTop: "0.5rem",
              }}
            >
              <tbody>
                <tr>
                  <td style={{ padding: "0.25rem 1rem", fontWeight: 600 }}>
                    Status
                  </td>
                  <td style={{ padding: "0.25rem 1rem" }}>{health.status}</td>
                </tr>
                <tr>
                  <td style={{ padding: "0.25rem 1rem", fontWeight: 600 }}>
                    Version
                  </td>
                  <td style={{ padding: "0.25rem 1rem" }}>{health.version}</td>
                </tr>
                <tr>
                  <td style={{ padding: "0.25rem 1rem", fontWeight: 600 }}>
                    Environment
                  </td>
                  <td style={{ padding: "0.25rem 1rem" }}>
                    {health.environment}
                  </td>
                </tr>
              </tbody>
            </table>
          </details>
        </div>
      )}
    </div>
  );
}
